import * as React from 'react';
import SvgIcon from '@mui/material/SvgIcon';

/**
 * Custom product icon set.
 *
 * One coherent family for the app's navigation surfaces (sidebar, goal menu,
 * page breadcrumbs) instead of mixed-metaphor Material icons. Shared design
 * language: 24x24 viewBox, 1.7px rounded strokes, stroke = currentColor so
 * they inherit color exactly like MUI icons (they are SvgIcon components,
 * so `sx` / `fontSize` / `color` props all behave the same).
 */

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};
const filled = { fill: 'currentColor', stroke: 'none' };

const make = (name, children) => {
  const Component = (props) => (
    <SvgIcon viewBox="0 0 24 24" {...props}>
      {children}
    </SvgIcon>
  );
  Component.displayName = name;
  return Component;
};

/* ------------------------------------------------------------------ */
/* Workspace navigation                                                */
/* ------------------------------------------------------------------ */

// Goals — bullseye: the target you are trying to hit.
export const GoalsIcon = make('GoalsIcon', (
  <g {...stroke}>
    <circle cx="12" cy="12" r="8.1" />
    <circle cx="12" cy="12" r="4.4" />
    <circle cx="12" cy="12" r="1.3" {...filled} />
  </g>
));

// Dashboard — masonry panels, echoing the actual dashboard layout.
export const DashboardIcon = make('DashboardIcon', (
  <g {...stroke}>
    <rect x="3.5" y="3.5" width="7.6" height="9.6" rx="1.8" />
    <rect x="13.1" y="3.5" width="7.4" height="6" rx="1.8" />
    <rect x="13.1" y="11.5" width="7.4" height="9" rx="1.8" />
    <rect x="3.5" y="15.1" width="7.6" height="5.4" rx="1.8" />
  </g>
));

// Data Sources — database cylinder: where raw data lives.
export const DataSourcesIcon = make('DataSourcesIcon', (
  <g {...stroke}>
    <ellipse cx="12" cy="5.9" rx="7" ry="2.6" />
    <path d="M5 5.9 V18.1" />
    <path d="M19 5.9 V18.1" />
    <path d="M5 18.1 a7 2.6 0 0 0 14 0" />
    <path d="M5 12 a7 2.6 0 0 0 14 0" />
  </g>
));

// Data Signals — a waveform pulse: the time series themselves.
export const DataSignalsIcon = make('DataSignalsIcon', (
  <g {...stroke}>
    <path d="M3 12.6 H6.1 L8.5 5.8 L12.3 18.4 L14.9 8.6 L16.5 12.6 H21" />
  </g>
));

// Discovery — magnifier over a tiny driver graph: hunting for insight.
// Shared with the goal-level "Driver Discovery" tab so both surfaces carry
// the same mark.
export const DiscoveryIcon = make('DiscoveryIcon', (
  <g {...stroke}>
    <circle cx="10.4" cy="10.4" r="6.4" />
    <path d="M15.2 15.2 L20.3 20.3" />
    <circle cx="8.3" cy="9" r="1.05" {...filled} />
    <circle cx="12.5" cy="11.9" r="1.05" {...filled} />
    <path d="M9.2 9.6 L11.6 11.3" />
  </g>
));

// Relationships — a small graph: nodes and the edges between them.
export const RelationshipsIcon = make('RelationshipsIcon', (
  <g {...stroke}>
    <circle cx="5.7" cy="7" r="2.1" />
    <circle cx="18.2" cy="5.5" r="2.1" />
    <circle cx="13.4" cy="18" r="2.1" />
    <path d="M7.8 6.7 L16.1 5.8" />
    <path d="M6.6 8.9 L12.5 16.2" />
    <path d="M17.5 7.4 L14.1 16.1" />
  </g>
));

// Analyst — chat bubble with an AI spark: ask, get answers.
export const AnalystIcon = make('AnalystIcon', (
  <g {...stroke}>
    <path d="M4 7 a3 3 0 0 1 3 -3 H17 a3 3 0 0 1 3 3 V13 a3 3 0 0 1 -3 3 H10.6 L6.6 19.6 V16 H7 a3 3 0 0 1 -3 -3 Z" />
    <path
      d="M12 6.9 c.34 1.85 1.31 2.82 3.16 3.16 c-1.85 .34 -2.82 1.31 -3.16 3.16 c-.34 -1.85 -1.31 -2.82 -3.16 -3.16 c1.85 -.34 2.82 -1.31 3.16 -3.16 Z"
      {...filled}
    />
  </g>
));

// Segments — a feature grid: rows x engineered columns, training-ready.
export const SegmentsIcon = make('SegmentsIcon', (
  <g {...stroke}>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
    <path d="M3.5 9.4 H20.5" />
    <path d="M9.8 9.4 V19.5" />
    <path d="M15.2 9.4 V19.5" />
  </g>
));

// Models — a cube: the model artifact.
export const ModelsIcon = make('ModelsIcon', (
  <g {...stroke}>
    <path d="M12 3 L20 7.5 V16.5 L12 21 L4 16.5 V7.5 Z" />
    <path d="M4 7.5 L12 12 L20 7.5" />
    <path d="M12 12 V21" />
  </g>
));

// Pipelines — a single bold route from source to destination, transit-map
// style: one stroke with two bends, endpoint dots. Stays crisp at rail size
// where multi-shape marks blur.
export const PipelinesIcon = make('PipelinesIcon', (
  <g {...stroke} strokeWidth={1.9}>
    <circle cx="5" cy="17.5" r="2.1" {...filled} />
    <path d="M7.1 17.5 H11 a2.4 2.4 0 0 0 2.4 -2.4 V8.9 a2.4 2.4 0 0 1 2.4 -2.4 H16.9" />
    <circle cx="19" cy="6.5" r="2.1" {...filled} />
  </g>
));

// Trainings — a loss curve on axes: steep drop, long flat tail, marker on
// the converged end.
export const TrainingsIcon = make('TrainingsIcon', (
  <g {...stroke}>
    <path d="M4.5 3.8 V19.7 H20.2" />
    <path d="M7.3 4.8 C7.9 12.6 9.6 15.4 18.6 15.9" />
    <circle cx="18.6" cy="15.9" r="1.45" {...filled} />
    <path d="M4.5 8.6 H6.1" />
    <path d="M4.5 13.2 H6.1" />
  </g>
));

// Deployments — a broadcast antenna: the model is live and serving.
export const DeploymentsIcon = make('DeploymentsIcon', (
  <g {...stroke}>
    <circle cx="12" cy="9.2" r="1.7" {...filled} />
    <path d="M8.2 5.4 a5.4 5.4 0 0 0 0 7.6" />
    <path d="M15.8 5.4 a5.4 5.4 0 0 1 0 7.6" />
    <path d="M5.6 3.2 a8.6 8.6 0 0 0 0 12" />
    <path d="M18.4 3.2 a8.6 8.6 0 0 1 0 12" />
    <path d="M12 11.4 V20" />
  </g>
));

// Inferences — a trending chart: bold zigzag climbing, marker dots at the
// pivots so it reads as plotted data rather than a bare line.
export const InferencesIcon = make('InferencesIcon', (
  <g {...stroke} strokeWidth={1.9}>
    <path d="M3.5 17.5 L9 10.8 L13.2 14.2 L20.5 5.8" />
    <circle cx="9" cy="10.8" r="1.5" {...filled} />
    <circle cx="13.2" cy="14.2" r="1.5" {...filled} />
  </g>
));

/* ------------------------------------------------------------------ */
/* Goal detail sections                                                */
/* ------------------------------------------------------------------ */

// Overview — a briefing sheet: the goal at a glance.
export const OverviewIcon = make('OverviewIcon', (
  <g {...stroke}>
    <rect x="4.5" y="3.5" width="15" height="17" rx="2" />
    <path d="M8 8.3 H16" />
    <path d="M8 12 H16" />
    <path d="M8 15.7 H12.5" />
  </g>
));

// Driver Discovery — same mark as workspace Discovery (one concept, one icon).
export const DriverDiscoveryIcon = DiscoveryIcon;

// Alerts — a bell.
export const AlertsIcon = make('AlertsIcon', (
  <g {...stroke}>
    <path d="M12 4 a5.3 5.3 0 0 1 5.3 5.3 c0 3.5 1 4.7 1.9 5.5 H4.8 c.9 -.8 1.9 -2 1.9 -5.5 A5.3 5.3 0 0 1 12 4 Z" />
    <path d="M10.2 18.1 a1.9 1.9 0 0 0 3.6 0" />
  </g>
));

// Activity — a history clock: what happened, and when.
export const ActivityIcon = make('ActivityIcon', (
  <g {...stroke}>
    <path d="M19.6 12 a7.6 7.6 0 1 1 -2.3 -5.5" />
    <path d="M19.8 3.6 V7 H16.4" />
    <path d="M12 8.2 V12 L14.9 13.8" />
  </g>
));

// Settings — mixer sliders.
export const SettingsIcon = make('SettingsIcon', (
  <g {...stroke}>
    <path d="M4 7 H7.4" />
    <path d="M11.2 7 H20" />
    <circle cx="9.3" cy="7" r="1.9" />
    <path d="M4 12 H12.2" />
    <path d="M16 12 H20" />
    <circle cx="14.1" cy="12" r="1.9" />
    <path d="M4 17 H6.4" />
    <path d="M10.2 17 H20" />
    <circle cx="8.3" cy="17" r="1.9" />
  </g>
));
